import useSWR, { SWRResponse } from "swr";

import { useSWRApiErrorHandler } from "../common/hooks";
import { AccountFormValues } from "../features/bankAccount/components/AccountForm";

import request, { ApiError } from "./common";
import { BankAccount, ResponseWithPagination } from "../types/common";

const path = "/bankacct";

const bankAccountApi = {
    create: (values: AccountFormValues): Promise<BankAccount> => {
        const profile = JSON.parse(values.profile);
        const addr = values.registeredAddress + " | " + values.city + " | " + values.countryCode;
        const payload: BankAccountPayload = {
            bankAccountNumber: values.accountNumber,
            bankName: values.bankName,
            bankAccountHolderName: values.holderName,
            currency: values.paymentCurrency,
            registeredAddress: addr,
            swiftAddress: values.swiftAddress,
            profileId: profile.id,
        };
        return request<BankAccount, BankAccountPayload>(
            `${path}/bank-account`,
            "POST",
            payload,
        );
    },
    update: (id: number, values: AccountFormValues): Promise<BankAccount> => {
        const userProfile = JSON.parse(values.profile);
        const addr = values.registeredAddress + " | " + values.city + " | " + values.countryCode;
        const payload: BankAccount = {
            id,
            bankAccountNumber: values.accountNumber,
            bankName: values.bankName,
            bankAccountHolderName: values.holderName,
            currency: values.paymentCurrency,
            registeredAddress: addr,
            swiftAddress: values.swiftAddress,
            userProfile: {
                contactPersonEmail: userProfile.contactPersonEmail,
                contactPersonName: userProfile.contactPersonName,
                contactPersonNumber: userProfile.contactPersonNumber,
                location: userProfile.location,
                name: userProfile.name,
                type: userProfile.type,
                accountName: userProfile.accountName,
                id: userProfile.id,
                wallet: userProfile.wallet,
            },
        };
        return request<BankAccount, BankAccount>(
            `${path}/bank-account`,
            "PUT",
            payload,
        );
    },
    filter: (userProfileId?: number): Promise<BankAccount[]> => {
        return request<BankAccount[], null>(
            userProfileId ? `${path}/bank-account?userProfileId=${userProfileId}` : `${path}/bank-account`,
            "GET",
            null,
        );
    },
    filterPaging: (page: number, size: number, userProfileId?: number): Promise<ResponseWithPagination<BankAccount[]>> => {
        return request<ResponseWithPagination<BankAccount[]>, null>(
            userProfileId ? `${path}/bank-account/paging?userProfileId=${userProfileId}&page=${page}&size=${size}` : `${path}/bank-account/paging?page=${page}&size=${size}`,
            "GET",
            null,
        );
    },
    deactivate: (id: string): Promise<BankAccount> => {
        return request<BankAccount, any>(
            `${path}/bank-account/deactive`,
            "POST",
            { id: id },
        );
    },
    review: (id: string, approvalStatus: number, rejectReason: string | null): Promise<BankAccount> => {
        const payload: ReviewApprovePayload = {
            id: id,
            approvalStatus: approvalStatus,
            rejectReason: rejectReason ? rejectReason : "",
        }
        return request<BankAccount, ReviewApprovePayload>(
            `${path}/bank-account/approve`,
            "POST",
            payload,
        );
    },
};

export const useBankAccountList = (): SWRResponse<BankAccount[], ApiError> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<BankAccount[], ApiError>(`${path}/bank-account`, request, {
        onError,
    });
};

export const useBankAccountListPaging = (): SWRResponse<ResponseWithPagination<BankAccount[]>, ApiError> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<ResponseWithPagination<BankAccount[]>, ApiError>(`${path}/bank-account`, request, {
        onError,
    });
};

export const useBankAccountById = (
    id: string,
): SWRResponse<BankAccount, ApiError> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<BankAccount, ApiError>(
        `${path}/bank-account/${id}`,
        request,
        {
            onError,
        },
    );
};

export type BankAccountPayload = {
    bankAccountHolderName: string;
    bankAccountNumber: string;
    swiftAddress: string;
    bankName: string;
    registeredAddress: string;
    currency: string;
    profileId: number;
};

type ReviewApprovePayload = {
    id: string;
    approvalStatus: number;
    rejectReason: string;
};

export default bankAccountApi;
